/**
 * ─── Nome de gente, escrito como nome ───
 *
 * O app antigo forçava `text-transform: lowercase` na tela inteira, então quem
 * se cadastrou por ele digitou "paulo" e nunca viu diferença. No design v2 o
 * texto sai como está no banco, e o cartão da conta abria com "paulo" em
 * minúsculo ao lado de "Editar perfil".
 *
 * A correção é de EXIBIÇÃO, e conservadora: só conserta quem está todo em
 * minúsculas, que é a marca de quem digitou sem pensar no assunto. "McDonald",
 * "d'Ávila" e "TIAGO" ficam exatamente como a pessoa escreveu, porque ali a
 * caixa foi escolha dela.
 */

/** Ligações que ficam minúsculas no meio do nome ("Maria da Silva"). */
const LIGACOES = new Set([
  "de",
  "da",
  "do",
  "das",
  "dos",
  "e",
  "di",
  "du",
  "van",
  "von",
  "del",
  "della",
  "la",
  "le",
])

/**
 * ─── O que cabe num nome de gente ───
 *
 * A MESMA regra de `api/src/helpers/nome-de-pessoa.ts`, com a mesma frase: mudou
 * lá, muda aqui. Em 23/10/2026 uma personal assinou com o nome
 * "A** **** ******* ******", e ele foi assim para a nota fiscal e para o Asaas.
 * O servidor recusa de qualquer jeito; o campo avisa antes de enviar, para a
 * pessoa corrigir ali mesmo.
 *
 * Cabe: letra de qualquer alfabeto (com acento), número, espaço, ponto, hífen,
 * apóstrofo (o reto, o curvo que o iPhone escreve sozinho e o acento agudo que
 * muita gente usa no lugar dele), parênteses, barra, & e vírgula. Não cabe:
 * emoji, sinal solto (* _ ~ | @ # aspas, sinal de HTML) e caractere invisível.
 * A frase diz QUAL sinal tirar: "use só letras" faz a pessoa procurar o erro num
 * nome que, para ela, está certo.
 */
const SINAIS_PERMITIDOS = new Set([".", "-", "'", "\u{2019}", "\u{B4}", "(", ")", "/", "&", ","])
const LETRA_OU_MARCA = /[\p{L}\p{Mn}\p{Mc}]/u
const PICTOGRAMA = /\p{Extended_Pictographic}/u
const INVISIVEL = /[\p{Cc}\p{Cf}]/u

/** Como o sinal aparece na frase: o nome que a pessoa conhece, com o desenho do lado. */
const NOME_DO_SINAL: Record<string, string> = {
  "*": "asterisco",
  "\u{2217}": "asterisco",
  "\u{204E}": "asterisco",
  "\u{2731}": "asterisco",
  "\u{FF0A}": "asterisco",
  "@": "arroba",
  "#": "hashtag",
  _: "sublinhado",
  "|": "barra vertical",
  "~": "til",
  "`": "acento grave",
  "^": "acento circunflexo",
  "!": "exclamação",
  "?": "interrogação",
  '"': "aspas",
  "\u{201C}": "aspas",
  "\u{201D}": "aspas",
  "<": "sinal de menor",
  ">": "sinal de maior",
  "=": "sinal de igual",
  "+": "sinal de mais",
  $: "cifrão",
  "%": "porcentagem",
  "[": "colchete",
  "]": "colchete",
  "{": "chave",
  "}": "chave",
  "\\": "barra invertida",
  ";": "ponto e vírgula",
  ":": "dois-pontos",
  "\u{2013}": "travessão",
  "\u{2014}": "travessão",
  "\u{2026}": "reticências",
  "\u{2022}": "bolinha",
}
/* O travessão vai só pelo nome: o projeto não escreve o sinal em texto nenhum. */
const SEM_DESENHO = new Set(["\u{2013}", "\u{2014}"])

function rotuloDoSinal(c: string): string {
  const nome = NOME_DO_SINAL[c]
  if (!nome) return `"${c}"`
  return SEM_DESENHO.has(c) ? nome : `${nome} (${c})`
}

function ehSeletorDeVariacao(cp: number): boolean {
  return (cp >= 0xfe00 && cp <= 0xfe0f) || (cp >= 0xe0100 && cp <= 0xe01ef)
}

function cabeNoNome(c: string): boolean {
  if (/\s/.test(c) || (c >= "0" && c <= "9") || SINAIS_PERMITIDOS.has(c)) return true
  return LETRA_OU_MARCA.test(c) && !ehSeletorDeVariacao(c.codePointAt(0)!)
}

/** As peças de um emoji: o desenho, a cor da pele, a junção, a bandeira e o seletor que vem junto. */
function ehPedacoDeEmoji(c: string): boolean {
  const cp = c.codePointAt(0)!
  return (
    cp === 0x200d ||
    cp === 0x20e3 ||
    ehSeletorDeVariacao(cp) ||
    (cp >= 0x1f3fb && cp <= 0x1f3ff) ||
    (cp >= 0x1f1e6 && cp <= 0x1f1ff) ||
    (cp >= 0xe0020 && cp <= 0xe007f) ||
    (cp > 0x7f && PICTOGRAMA.test(c))
  )
}

function juntar(itens: string[]): string {
  return itens.length === 1 ? itens[0] : `${itens.slice(0, -1).join(", ")} nem ${itens[itens.length - 1]}`
}

/** A frase do campo quando o nome tem o que não cabe, ou `null` quando está tudo certo. */
export function problemaNoNome(
  texto: string | null | undefined,
  campo: "nome" | "sobrenome" = "nome",
): string | null {
  if (!texto) return null
  const sinais: string[] = []
  let emoji = false
  let invisivel = false
  for (const c of Array.from(texto)) {
    if (cabeNoNome(c)) continue
    if (ehPedacoDeEmoji(c)) emoji = true
    else if (INVISIVEL.test(c)) invisivel = true
    else if (!sinais.includes(c)) sinais.push(c)
  }
  if (!sinais.length && !emoji && !invisivel) return null

  /* Um rótulo por nome: as aspas de abrir e de fechar aparecem uma vez só. */
  const porNome = new Map<string, string>()
  for (const c of sinais) if (!porNome.has(NOME_DO_SINAL[c] ?? c)) porNome.set(NOME_DO_SINAL[c] ?? c, rotuloDoSinal(c))
  const rotulos = Array.from(porNome.values())
  const itens = rotulos.slice(0, 3)
  if (rotulos.length > 3) itens.push("outros sinais")
  if (emoji) itens.push("emoji")
  if (invisivel) itens.push("caractere invisível")
  return `O ${campo} não pode ter ${juntar(itens)}: escreva como está no documento.`
}

export function nomeProprio(texto?: string | null): string {
  if (!texto) return ""
  const limpo = texto.trim()
  if (!limpo || limpo !== limpo.toLowerCase()) return limpo

  return limpo
    .split(/(\s+)/)
    .map((parte, i) => {
      if (!parte.trim()) return parte
      // A primeira palavra sobe sempre: "da Silva" sozinho não é nome.
      if (i > 0 && LIGACOES.has(parte)) return parte
      return parte.charAt(0).toUpperCase() + parte.slice(1)
    })
    .join("")
}
